import { useEffect, useState } from "react";
import Card from "../components/Card";
import { listWorksites } from "../services/api";
import { loadSession } from "../auth";
import { useAdminScope } from "../hooks/useAdminScope";
import { useTranslation } from "react-i18next";

// ─── Empty section ───────────────────────────────────────────────────────────

function EmptyDocSection({ t }) {
  return (
    <div className="py-8 text-center text-sm text-slate-400">
      {t("docs.no_documents")}
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function WorksiteDocumentation() {
  const { t } = useTranslation();
  const session = loadSession();
  const me = session?.user;

  const isOrgAdmin =
    me?.derived?.isOrgAdmin ||
    (Array.isArray(me?.roles) && me.roles.includes("org_super_admin"));

  const myWorksiteId = me?.worksite_id ?? null;
  const isWorksiteScoped = isOrgAdmin && myWorksiteId !== null;

  const { worksiteId, setWorksiteId } = useAdminScope(me?.id);

  const [worksites, setWorksites] = useState([]);
  const [wsLoading, setWsLoading] = useState(false);

  useEffect(() => {
    if (!isOrgAdmin || isWorksiteScoped) return;
    setWsLoading(true);
    listWorksites()
      .then((data) => setWorksites(data.items || []))
      .catch(() => setWorksites([]))
      .finally(() => setWsLoading(false));
  }, [isOrgAdmin, isWorksiteScoped]);

  // Validate saved worksite selection against the loaded list
  useEffect(() => {
    if (!worksiteId || worksites.length === 0) return;
    const ids = worksites.map((w) => String(w.id ?? ""));
    if (!ids.includes(String(worksiteId))) setWorksiteId("");
  }, [worksites, worksiteId, setWorksiteId]);

  if (!isOrgAdmin) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-6">
        <Card>
          <div className="text-sm text-slate-600">{t("admin_users.need_role")}</div>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{t("worksite_docs.title")}</h1>
          {isWorksiteScoped && me?.worksite?.name && (
            <div className="text-sm text-slate-500 mt-0.5">
              {t("worksite_docs.worksite_label")}:{" "}
              <span className="font-medium text-slate-700">{me.worksite.name}</span>
            </div>
          )}
        </div>

        {/* Worksite filter — tenant-wide admins only */}
        {!isWorksiteScoped && (
          <div>
            {wsLoading ? (
              <span className="text-sm text-slate-500">{t("admin_users.loading")}</span>
            ) : worksites.length === 0 ? (
              <span className="text-sm text-slate-400">{t("worksite_docs.no_worksites")}</span>
            ) : (
              <select
                className="px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                value={worksiteId}
                onChange={(e) => setWorksiteId(e.target.value)}
              >
                <option value="">{t("worksite_docs.all_worksites")}</option>
                {worksites.map((ws) => (
                  <option key={ws.id} value={String(ws.id)}>
                    {ws.name}
                  </option>
                ))}
              </select>
            )}
          </div>
        )}
      </div>

      {/* SSM Documentation */}
      <Card title={t("docs.ssm_title")}>
        <EmptyDocSection t={t} />
      </Card>

      {/* SU Documentation */}
      <Card title={t("docs.su_title")}>
        <EmptyDocSection t={t} />
      </Card>

      {/* RSVTI Documentation */}
      <Card title={t("docs.rsvti_title")}>
        <EmptyDocSection t={t} />
      </Card>

      {/* Note de lucru */}
      <Card title={t("docs.work_notes_title")}>
        <EmptyDocSection t={t} />
      </Card>
    </div>
  );
}
